import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Trash2, Copy, Check, Link, X, Pencil, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import WebhookTestPanel from './WebhookTestPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://tracjobsalert.co.uk';

export default function ReferralsTab({ token }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [copiedDashboard, setCopiedDashboard] = useState(null);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', discount_code: '' });

  const fetchPartners = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/referral-partners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPartners(data);
    } catch (err) {
      toast.error('Failed to load referral partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      toast.error('Name and slug are required');
      return;
    }
    setSaving(true);
    try {
      const url = editingId 
        ? `${API_URL}/api/admin/referral-partners/${editingId}`
        : `${API_URL}/api/admin/referral-partners`;
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Server returned invalid response');
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed');
      }
      
      toast.success(editingId ? 'Partner updated' : 'Partner created');
      setForm({ name: '', slug: '', discount_code: '' });
      setShowForm(false);
      setEditingId(null);
      fetchPartners();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete referral partner "${name}"? This won't affect existing tracked users.`)) return;
    try {
      await fetch(`${API_URL}/api/admin/referral-partners/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Partner deleted');
      fetchPartners();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      await fetch(`${API_URL}/api/admin/referral-partners/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive })
      });
      toast.success(`Partner ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchPartners();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleRemoveDiscount = async (id) => {
    try {
      await fetch(`${API_URL}/api/admin/referral-partners/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount_code: '' })
      });
      toast.success('Discount code removed (link still tracks referrals)');
      fetchPartners();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${SITE_URL}/r/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const copyDashboardLink = (partner) => {
    const dashboardUrl = `${SITE_URL}/affiliate/${partner.slug}?key=${partner.dashboard_key}`;
    navigator.clipboard.writeText(dashboardUrl);
    setCopiedDashboard(partner.id);
    toast.success('Dashboard link copied! Share this with your affiliate.');
    setTimeout(() => setCopiedDashboard(null), 2000);
  };

  const regenerateDashboardKey = async (partnerId) => {
    if (!window.confirm('Are you sure? This will invalidate the current dashboard link.')) return;
    setRegeneratingKey(partnerId);
    try {
      const res = await fetch(`${API_URL}/api/admin/referral-partners/${partnerId}/regenerate-key`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to regenerate key');
      toast.success('New dashboard key generated');
      fetchPartners();
    } catch (err) {
      toast.error('Failed to regenerate key');
    } finally {
      setRegeneratingKey(null);
    }
  };

  const startEdit = (partner) => {
    setForm({ name: partner.name, slug: partner.slug, discount_code: partner.discount_code || '', commission_rate: partner.commission_rate || 0 });
    setEditingId(partner.id);
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Create/Edit Form */}
      {showForm && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{editingId ? 'Edit Partner' : 'Add Referral Partner'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-300">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Dr Smith"
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Slug (URL path)</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="drsmith"
                    disabled={!!editingId}
                    className="bg-slate-700 border-slate-600 text-white disabled:opacity-50"
                    data-testid="partner-slug-input"
                  />
                  {form.slug && <p className="text-xs text-slate-400 mt-1">{SITE_URL}/r/{form.slug}</p>}
                </div>
                <div>
                  <Label className="text-slate-300">Discount Code (optional)</Label>
                  <Input
                    value={form.discount_code}
                    onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })}
                    placeholder="e.g. DRSMITH20"
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="partner-discount-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Must exist in Discounts tab first</p>
                </div>
                <div>
                  <Label className="text-slate-300">Commission Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.commission_rate || ''}
                    onChange={(e) => setForm({ ...form, commission_rate: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g. 10"
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="partner-commission-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">% of each payment</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="save-partner-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Create Partner'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); setForm({ name: '', slug: '', discount_code: '' }); }} className="text-slate-400">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Partners List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Referral Partners ({partners.length})</CardTitle>
          {!showForm && (
            <Button onClick={() => { setShowForm(true); setEditingId(null); setForm({ name: '', slug: '', discount_code: '' }); }} className="bg-blue-600 hover:bg-blue-500" data-testid="add-partner-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Partner
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {partners.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No referral partners yet. Add one to start tracking.</p>
          ) : (
            <div className="space-y-3">
              {partners.map((partner) => (
                <div key={partner.id} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{partner.name}</p>
                          <Badge className={partner.active ? 'bg-green-600' : 'bg-slate-500'}>
                            {partner.active ? 'Active' : 'Inactive'}
                          </Badge>
                          {partner.discount_code && (
                            <Badge className="bg-purple-600">{partner.discount_code}</Badge>
                          )}
                        </div>
                        {/* Referral Link */}
                        <div className="flex items-center gap-2 mt-2">
                          <Link className="w-3 h-3 text-slate-400" />
                          <span className="text-sm text-slate-400">{SITE_URL}/r/{partner.slug}</span>
                          <button onClick={() => copyLink(partner.slug)} className="text-slate-400 hover:text-white" title="Copy referral link">
                            {copiedSlug === partner.slug ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                        {/* Dashboard Link - More Prominent */}
                        {partner.dashboard_key && (
                          <div className="mt-2 p-2 bg-slate-800 rounded-md border border-slate-600">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-amber-400 font-medium mb-1">Affiliate Dashboard Link</p>
                                <code className="text-xs text-slate-300 block truncate">
                                  {SITE_URL}/affiliate/{partner.slug}?key={partner.dashboard_key}
                                </code>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button 
                                  size="sm" 
                                  onClick={() => copyDashboardLink(partner)} 
                                  className={`h-7 px-2 ${copiedDashboard === partner.id ? 'bg-green-600' : 'bg-amber-600 hover:bg-amber-500'}`}
                                  title="Copy dashboard link to share with affiliate"
                                >
                                  {copiedDashboard === partner.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  <span className="ml-1 text-xs">{copiedDashboard === partner.id ? 'Copied!' : 'Copy'}</span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => regenerateDashboardKey(partner.id)}
                                  disabled={regeneratingKey === partner.id}
                                  className="h-7 px-2 text-slate-400 hover:text-white"
                                  title="Regenerate dashboard key (invalidates current link)"
                                >
                                  <RefreshCw className={`w-3 h-3 ${regeneratingKey === partner.id ? 'animate-spin' : ''}`} />
                                </Button>
                                <a
                                  href={`${SITE_URL}/affiliate/${partner.slug}?key=${partner.dashboard_key}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="h-7 px-2 text-slate-400 hover:text-white inline-flex items-center"
                                  title="Open dashboard in new tab"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Stats */}
                      <div className="text-right">
                        <div className="flex gap-4">
                          <div>
                            <p className="text-xs text-slate-400">Clicks</p>
                            <p className="text-lg font-bold text-white">{partner.clicks || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Signups</p>
                            <p className="text-lg font-bold text-blue-400">{partner.signups || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Paid</p>
                            <p className="text-lg font-bold text-green-400">{partner.conversions || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Rate</p>
                            <p className="text-lg font-bold text-slate-300">{partner.commission_rate || 0}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Earned</p>
                            <p className="text-lg font-bold text-amber-400">£{(partner.total_commission_earned || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(partner)} className="text-slate-400 hover:text-blue-400 h-8 px-2" data-testid={`edit-partner-${partner.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {partner.discount_code && (
                          <Button size="sm" variant="ghost" onClick={() => handleRemoveDiscount(partner.id)} className="text-slate-400 hover:text-yellow-400 h-8 px-2" title="Remove discount code (keep tracking)">
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleToggleActive(partner.id, partner.active)} className={`h-8 px-2 ${partner.active ? 'text-slate-400 hover:text-yellow-400' : 'text-slate-400 hover:text-green-400'}`}>
                          {partner.active ? 'Pause' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(partner.id, partner.name)} className="text-slate-400 hover:text-red-400 h-8 px-2">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {/* Converted users */}
                  {partner.converted_users && partner.converted_users.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-600">
                      <p className="text-xs text-slate-400 mb-1">Paid subscribers:</p>
                      <div className="flex flex-wrap gap-2">
                        {partner.converted_users.map((u, i) => (
                          <Badge key={i} className="bg-slate-600 text-xs">{u.email}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Test Panel */}
      <WebhookTestPanel token={token} />
    </div>
  );
}
